<template>
    <div id="pose_selection">

        <div class="data-loading text-center mt-5" v-if="loading">
            <font-awesome-icon pulse :icon="['fas', 'spinner']" size="3x"></font-awesome-icon>
        </div>

        <div v-else>
            <HeadFoot title="Select Your Poses" v-on:back="back">
                <button type="button" class="font-medium transition-all duration-300 border-2 rounded-md inline-block py-2 px-3 md:px-4 bg-leonard-green border-transparent text-leonard-white hover:bg-leonard-green-hover focus:ring-leonard-green cursor-pointer disabled:bg-leonard-green-disabled disabled:text-leonard-green disabled:cursor-not-allowed" @click="addToCart" aria-label="Add To Cart" :disabled="adding || notReady">
                    Add to Cart
                    <font-awesome-icon spin :icon="['fas', 'spinner']" class="ml-1" v-if="adding"></font-awesome-icon>
                </button>
            </HeadFoot>

            <div class="flex justify-center items-center w-full lg:w-3/4 lg:mx-auto mb-12">
                <div class="mr-3" v-if="pkg_no.split('')[0] === 'C'">
                    <img :src="'/images/'+pkg_no+'.jpg'" :alt="pkg_no+' photo order'" width="150">
                </div>

                <div class="w-full">
                    <h3 class="mb-1 uppercase">{{details.flyername}}</h3>
                    <p>{{details.description}}</p>

                    <h4 class="mt-4 font-semibold">Package Contents:</h4>
                    <ul class="px-4 md:columns-2">
                        <li v-for="({name}, i) in details.contents" :key="`item-${i}`">
                            <font-awesome-icon :icon="['fas', 'check']" size="sm" class="mr-2 text-leonard-green"></font-awesome-icon>
                            {{name}}
                        </li>
                    </ul>
                </div>
            </div>

            <Alert type="warning" align="left" title="Caution" v-if="details.pose_limit !== 'Unlimited' && favorites.length < details.pose_limit">
                This package has more pose slots than your selected favorites!<br />
                <button class="btn text-orange-900 underline p-0" @click="goToFavorites">Click here</button> to go back to select more favorites to get the most out of this package.
            </Alert>

            <div class="w-full lg:w-3/4 mx-auto mt-2 mb-5">
                <div class="w-full mb-2">
                    <div class="text-left">
                        <h4 class="text-leonard-green">Currently Used Poses</h4>
                        <div class="bg-leonard-info text-leonard-white text-center w-full px-4 py-3 my-2" v-if="details.pose_limit !== 'Unlimited'">
                            <p class="text-sm" v-if="setByPreviousChoices">The poses here are shown based on your previous selections for this package.</p>
                            <p class="text-sm" v-else>We've pre-selected poses for this package based on your favorites, up to the pose limit.</p>
                            <p class="text-sm">Click on any photo to swap it with another and replace the pose on the sheets below.</p>
                        </div>
                        <div class="bg-leonard-info-overlay text-leonard-white text-center w-full px-4 py-2" v-if="details.pose_limit === 'Unlimited'">
                            <p class="text-sm">This is your list of favorite poses available for you to use on any of the sheets below.</p>
                        </div>
                    </div>
                </div>
                <div class="flex flex-wrap justify-around items-center" v-if="details.pose_limit !== 'Unlimited'">
                    <figure class="relative cursor-pointer w-1/5" v-for="sel in selected_poses" :key="sel.f_num">
                        <img class="w-full" :src="sel.path" :alt="sel.photo" data-toggle="modal" data-target="#swap_pose" @click="setSwapable(sel)">
                        <figcaption class="absolute bottom-0 inset-x-0 text-center text-white text-shadow font-medium">
                            {{imageFNum(sel)}}
                        </figcaption>
                    </figure>
                </div>
                <div class="flex flex-wrap justify-around items-center p-0" v-if="details.pose_limit === 'Unlimited'">
                    <img style="width: calc(90% / 8);min-width:85px;" class="my-1 cursor-pointer" :src="sel.path" :alt="sel.photo" v-for="sel in selected_poses" :key="sel.f_num">
                </div>
            </div>

            <div class="mt-4">
                <PoseSelectionCard
                    :key="pkg_key"
                    :title="details.flyername"
                    :items="details.contents.filter(({pose_select_req, conttype, per_sheet, download, qty_sheets}) => (pose_select_req === 1 && ['PHOTO', 'OUTSOURCE'].includes(conttype) && parseInt(per_sheet) > 0 && parseInt(qty_sheets) < 99) || (pose_select_req === 1 && download === 1 && parseInt(qty_sheets) === 1 && parseInt(per_sheet) > 0))"
                    :previouslySelected="setByPreviousChoices"
                    v-if="details.contents && details.contents.length > 0"
                    v-on:setChangable="setChangable"
                ></PoseSelectionCard>

                <div class="w-full my-3 text-right">
                    <p class="mb-2"><strong>Package:</strong> ${{details.net_price.toFixed(2)}}</p>
                    <p><strong>Subtotal:</strong> ${{parseFloat(details.net_price).toFixed(2)}}</p>
                </div>
            </div>

            <HeadFoot title="Select Your Poses" v-on:back="back">
                <button type="button" class="font-medium transition-all duration-300 border-2 rounded-md inline-block py-2 px-3 md:px-4 bg-leonard-green border-transparent text-leonard-white hover:bg-leonard-green-hover focus:ring-leonard-green cursor-pointer disabled:bg-leonard-green-disabled disabled:text-leonard-green disabled:cursor-not-allowed" @click="addToCart" :disabled="adding || notReady">
                    Add to Cart
                    <font-awesome-icon spin :icon="['fas', 'spinner']" class="ml-1" v-if="adding"></font-awesome-icon>
                </button>
            </HeadFoot>
        </div>

        <!-- Modal for changing single poses -->
        <ChangePoseModal
            :key="modal_key"
            :maxpose="details.pose_limit"
            :current="selected_poses"
            :choosable="$route.params.package.split('')[0] === 'C' ? favorites : selected_poses"
            :change="changing"
            v-if="changing.pose"
            v-on:goToFavorites="goToFavorites"
            v-on:updatePose="updatePose"
        ></ChangePoseModal>

        <SwapPoseModal
            :key="swap_key"
            :current="selected_poses"
            :favorites="favorites"
            :change="swapping"
            v-if="swapping.f_num"
            v-on:goToFavorites="goToFavorites"
            v-on:swapPose="swapPose"
        ></SwapPoseModal>
    </div>
</template>

<script>
    import {library} from '@fortawesome/fontawesome-svg-core';
    import {faCheck, faInfoCircle, faSpinner} from '@fortawesome/free-solid-svg-icons';
    import {faHandPointer} from '@fortawesome/free-regular-svg-icons';

    import Cart from '../../classes/Cart';
    import Order from '../../classes/Order';
    import Photo from '../../classes/Photo';
    import Package from '../../classes/Package';

    import Alert from '../../components/app/Alert';
    import HeadFoot from './partials/StepHeaderFooter';
    import SwapPoseModal from './partials/SwapPoseModal';
    import ChangePoseModal from './partials/ChangePoseModal';
    import PoseSelectionCard from './partials/PoseSelectionCard';

    export default {
        data() {
            return {
                Cart: new Cart(),
                Order: new Order(),
                Photo: new Photo(),
                Package: new Package(),
                con_no: localStorage.getItem('con_no'),
                school: localStorage.getItem('school'),
                access_code: localStorage.getItem('access_code'),
                pkg_no: this.$route.params.package,
                step: this.$route.params.step,
                details: {},
                changing: {},
                swapping: {},
                favorites: [],
                selected_poses: [],
                retouched: [],
                retouched_complete: [],
                loading: true,
                adding: false,
                notReady: true,
                setByPreviousChoices: false,
                pkg_key: 0,
                modal_key: 200,
                swap_key: 300,
                retouch_key: 400,
            }
        },

        components: {
            Alert,
            HeadFoot,
            SwapPoseModal,
            ChangePoseModal,
            PoseSelectionCard,
        },

        created() {
            library.add(faSpinner, faCheck, faHandPointer, faInfoCircle);

            if(this.access_code != null) {
                this.getPackageDetails();
            } else {
                let alert = {
                    show: true,
                    level: 'danger',
                    text: 'No access code was found'
                };

                this.$emit('alertStatus', alert);
                this.$router.push({params: {step: null, package: null}});
            }
        },

        updated() {
            $('[data-toggle="popover"]').popover()
        },

        beforeRouteLeave (to, from, next) {
            this.$route.params.package = null;
            next();
        },

        methods: {
            imageFNum(pose) {
                return parseInt(pose.film).toString().substr(-2) + '-' + parseInt(pose.frame).toString().padStart(3, '0');
            },

            getPackageDetails() {
                let self = this;
                let data = {
                    con_no: this.con_no,
                };

                if(self.step === 'addons') {
                    data['addons'] = true;
                }

                this.Package.getDetails(this.pkg_no, data).then(function(response) {
                    self.setByPreviousChoices = false;
                    self.details = response.data;
                    self.getFavoriteImages();
                });
            },

            getFavoriteImages() {
                let self = this;
                let data = {
                    con_no: this.con_no,
                    user_id: this.$auth.user().details.id,
                    access_code: this.access_code
                };

                this.Photo.favoriteImages(data).then(function(response) {
                    let favs = response.data;

                    self.favorites = favs;

                    // if(!localStorage.getItem(self.access_code + '-' + self.pkg_no + '-selected')) {
                        // console.log('setting selected from favorites');

                        self.selected_poses = favs.slice(favs, self.details.pose_limit === 'Unlimited' ? 99 : self.details.pose_limit);

                        let selected = [];
                        self.selected_poses.forEach(pose => {
                            selected.push(pose.f_num);
                        });

                        self.setPhotos();
                    // } else {
                    //     console.log('setting selected from localstorage');

                    //     self.getUsedPhotosFromLocalStorage();
                    // }

                    window.events.$emit('finishedLoading');
                    self.loading = false;
                })
            },

            setUsedPhotosInLocalStorage() {
                let products = this.details.contents.filter(function({pose_select_req, conttype, per_sheet, download, qty_sheets}) {
                    return (pose_select_req === 1 && ['PHOTO', 'OUTSOURCE'].includes(conttype) && parseInt(per_sheet) > 0 && parseInt(qty_sheets) < 99) || (pose_select_req === 1 && download === 1 && parseInt(qty_sheets) === 1);
                });

                let selectedPoses = [];
                products.forEach((product, i) => {
                    let poseSet = [];
                    let sheetTotal = product.sheetPoses.length;

                    product.sheetPoses.forEach((pose, j) => {
                        poseSet.push(pose.f_num);
                        if(sheetTotal === j+1) {
                            selectedPoses.push(poseSet);
                        }
                    })
                });

                localStorage.setItem(
                    this.access_code + '-' + this.pkg_no + '-selected',
                    JSON.stringify(selectedPoses)
                );
            },

            getUsedPhotosFromLocalStorage() {
                let self = this;
                let selectedPoses = JSON.parse(localStorage.getItem(self.access_code + '-' + self.pkg_no + '-selected'));
                let processed = 0;

                selectedPoses.forEach((poseSet, i) => {
                    let poses = [];
                    let product = self.details.contents.filter(function({pose_select_req, conttype, per_sheet, download, qty_sheets}) {
                        return (pose_select_req === 1 && ['PHOTO', 'OUTSOURCE'].includes(conttype) && parseInt(per_sheet) > 0 && parseInt(qty_sheets) < 99) || (pose_select_req === 1 && download === 1 && parseInt(qty_sheets) === 1);
                    })[i];

                    poseSet.forEach((fnum, j) => {
                        let film = fnum.split('-')[0].slice(1);
                        let frame = fnum.split('-')[1].slice(-4);

                        poses.push({
                            f_num: fnum,
                            film,
                            frame
                        });

                        if(self.details.pose_limit === 'Unlimited') {
                            self.selected_poses = self.favorites.slice(self.favorites, 99);
                        }

                        if(self.selected_poses.filter(obj => obj.f_num === fnum).length === 0) {
                            let match = self.favorites.find(obj => obj.f_num === fnum);
                            self.selected_poses.push({
                                film,
                                frame,
                                f_num: fnum,
                                path: match.path,
                            })
                        }
                    });

                    product.sheetPoses = [];
                    self.getSheet(poses, product.per_sheet, product.qty_sheets).then(function(slots) {
                        product.sheetPoses = slots;
                        self.pkg_key++;
                        processed++;
                        if(selectedPoses.length === processed) {
                            self.notReady = false;
                        }
                    });
                });
            },

            getUsedPhotos() {
                let self = this;
                return this.Cart.getUsed(this.$auth.user().details.id).then(function(resp) {
                    const {used, retouched} = resp.data;
                    self.retouched = retouched;
                    return used;
                });
            },

            setPhotos() {
                // GET THE PHOTO PREVIEWS FROM JEEVES
                let self = this;
                let poses = [];

                this.selected_poses.forEach(function(pose) {
                    if(pose === undefined) return;
                    poses.push({
                        f_num: pose.f_num,
                        film: pose.film,
                        frame: pose.frame,
                    });
                });

                let pkg_items = this.details.contents.filter(function({pose_select_req, conttype, per_sheet, download, qty_sheets}) {
                    return (pose_select_req === 1 && ['PHOTO', 'OUTSOURCE'].includes(conttype) && parseInt(per_sheet) > 0 && parseInt(qty_sheets) < 99) || (pose_select_req === 1 && download === 1 && parseInt(qty_sheets) === 1);
                });

                let photosProcessed = 0;
                // console.log(pkg_items.length);

                pkg_items.forEach(function(sheet) {
                    const {per_sheet, qty_sheets} = sheet;

                    sheet.sheetPoses = [];
                    self.getSheet(poses, per_sheet, qty_sheets).then(function(slots) {
                        sheet.sheetPoses = slots;
                        self.pkg_key++;
                        photosProcessed++;
                        if(pkg_items.length === photosProcessed) {
                            self.notReady = false;
                            // self.setUsedPhotosInLocalStorage();
                        }
                    });
                });
            },

            getSheet(poses, perSheet, sheetQty) {
                let data = {
                    poses: poses.slice(0, sheetQty),
                    perSheet: perSheet,
                    sheetQty: sheetQty,
                };

                return this.Photo.getAsSheet(data).then(function(response) {
                    return response.data.slots;
                });
            },

            setChangable(item, pic_index) {
                this.modal_key += 1;
                this.changing = {
                    sheet: item.name,
                    sheet_index: pic_index,
                    pose: item.sheetPoses[pic_index-1],
                }
            },

            updatePose(pose_obj) {
                let self = this;
                let items = this.details.contents;

                let poses = [{
                    f_num: pose_obj.f_num,
                    film: pose_obj.film,
                    frame: pose_obj.frame,
                }];

                items.forEach(pkg_item => {
                    if(pkg_item.name === pose_obj.sheet) {
                        self.getSheet(poses, pkg_item.per_sheet, pkg_item.qty_sheets).then(function(slots) {
                            pkg_item.sheetPoses[pose_obj.sheet_index - 1] = slots[0];
                            self.pkg_key++;
                            self.checkUsed();
                        }).then(function() {
                            self.setByPreviousChoices = false;
                            // self.setUsedPhotosInLocalStorage();
                        });
                    }
                });

                let selection_obj = {
                    f_num: pose_obj.f_num,
                    film: pose_obj.film,
                    frame: pose_obj.frame,
                    is_selected: true,
                    path: pose_obj.path
                };

                let found = this.selected_poses.find(pose => pose.f_num === pose_obj.f_num);
                if(!found) this.selected_poses.push(selection_obj);

                this.retouch_key++;
            },

            setSwapable(pose) {
                this.swap_key += 1;
                this.swapping = pose;
            },

            swapPose(old_pose, new_pose) {
                let self = this;

                this.selected_poses.filter((o, i) => {
                    if(o.f_num === old_pose.f_num) self.selected_poses[i] = new_pose;
                });

                // this.setPhotos();

                let newpose = [{
                    f_num: new_pose.f_num,
                    film: new_pose.film,
                    frame: new_pose.frame,
                }];

                let pkg_items = this.details.contents.filter(function({pose_select_req, conttype, per_sheet, download, qty_sheets}) {
                    return (pose_select_req === 1 && ['PHOTO', 'OUTSOURCE'].includes(conttype) && parseInt(per_sheet) > 0 && parseInt(qty_sheets) < 99) || (pose_select_req === 1 && download === 1 && parseInt(qty_sheets) === 1);
                });

                pkg_items.forEach((pkg_item, i) => {
                    pkg_item.sheetPoses.filter((pose, j) => {
                        if(pose.f_num === old_pose.f_num) {
                            const {per_sheet, qty_sheets} = pkg_item;
                            self.getSheet(newpose, per_sheet, qty_sheets).then(function(slots) {
                                pkg_item.sheetPoses[j] = slots[0];
                                self.pkg_key++;
                            }).then(function() {
                                self.setByPreviousChoices = false;
                                // self.setUsedPhotosInLocalStorage();
                            });
                        }
                    })
                });

                this.retouch_key++;
            },

            checkUsed() {
                let self = this;
                let isUsedArray = [];

                let pkg_items = this.details.contents.filter(function({pose_select_req, conttype, per_sheet, download, qty_sheets}) {
                    return (pose_select_req === 1 && ['PHOTO', 'OUTSOURCE'].includes(conttype) && parseInt(per_sheet) > 0 && parseInt(qty_sheets) < 99) || (pose_select_req === 1 && download === 1 && parseInt(qty_sheets) === 1);
                });
                pkg_items.forEach(item => {
                    item.sheetPoses.filter(pose => isUsedArray.includes(pose.f_num) ? false : isUsedArray.push(pose.f_num));
                });

                if(this.details.pose_limit !== 'Unlimited') this.selected_poses.forEach((sel,i)=> isUsedArray.includes(sel.f_num) ? false : self.selected_poses.splice(i, 1));
            },


            goToFavorites() {
                $('.modal').modal('hide');
                this.$emit('goToStep', 'favorites', {return: this.step, pkg: this.pkg_no});
            },

            addToCart() {
                this.adding = true;

                let self = this;

                // Add the contents of the items with poses
                let contentsArray = [];
                let pkg_items = this.details.contents.filter(function({pose_select_req, conttype, per_sheet, download, qty_sheets}) {
                    return (pose_select_req === 1 && ['PHOTO', 'OUTSOURCE'].includes(conttype) && parseInt(per_sheet) > 0 && parseInt(qty_sheets) < 99) || (pose_select_req === 1 && download === 1 && parseInt(qty_sheets) === 1);
                });
                pkg_items.forEach(item => contentsArray.push({name: item.name, cont_code: item.cont_code, poses: item.sheetPoses.map(pose => pose.f_num), download: item.download, rush: 0}));

                // Add the contents of the items without poses
                this.details.contents.filter(function(item) {
                    return !contentsArray.map(function(content) {
                        return content.name
                    }).includes(item.name)
                }).forEach(function(item) {
                    contentsArray.push({name: item.name, cont_code: item.cont_code, poses: [], download: item.download, rush: 0});
                });

                let data = {
                    user_id: this.$auth.user().details.id,
                    name: this.details.flyername,
                    con_no: this.con_no,
                    school: this.school,
                    accesscode: this.access_code,
                    price: parseFloat(this.details.net_price).toFixed(2),
                    tax_amt: parseFloat(this.details.tax_amt).toFixed(2),
                    extra_poses: 0,
                    package_number: this.pkg_no,
                    contents: contentsArray,
                };

                this.Cart.addItem(data, data.user_id).then(function(response) {
                    self.addToCart = false;
                    if(!response.success) return;

                    window.events.$emit('addToCart');
                    // Show modal with option to continue or select another package
                    feedback('Package successfully added to cart');
                    self.next();
                }).catch(function(error) {
                    console.error(error);
                });
            },

            next() {
                this.$emit('goToStep', 'addons');
            },

            back() {
                if(this.pkg_no) {
                    this.$router.back();
                    return;
                }

                this.$emit('goToStep', 'packages');
            }
        },
    }
</script>
